import { z } from "zod";
// The wire shapes of the staff quote workflow (CHT-757; contract `docs/architecture/milestone-e-payments-contract.md`, section 2.5).
//
// Rules that hold for every schema in this file:
// - A body is a STRICT object. It never names a payment amount, a payment status, a provider order, a fingerprint or an expiry of a
//   payment request. The ONE amount a person types is the supplier's own quote (`amountMinor` of a quote body, as at S6); a payment
//   request made from a quote has no amount at all, and the server copies it from the approved quote.
// - Money is an integer number of paise. No float.
// - `supplierEvidenceReference` appears only in the STAFF view. No customer shape in this package carries it.
// - A STAFF view is never served to a customer.
const id = z.string().min(1).max(64);
const code = z.string().min(1).max(64);
const instant = z.coerce.date();
const version = z.number().int().positive();
export const STAFF_QUOTE_ROUTE_PATHS = Object.freeze({
    list: "/staff/concierge-requests/:requestId/quotes",
    record: "/staff/concierge-requests/:requestId/quotes",
    supersede: "/staff/payment-quotes/:quoteId/supersede",
    issuePayment: "/staff/payment-quotes/:quoteId/payment-requests",
});
/** The customer-visible terms of a quote: what the price covers and its conditions. One line, 1 to 500 characters. */
export const QUOTE_CUSTOMER_TERMS_MAX_LENGTH = 500;
const customerTerms = z.string().trim().min(1).max(QUOTE_CUSTOMER_TERMS_MAX_LENGTH);
const quoteEvidence = z.strictObject({
    kind: z.enum(["SUPPLIER_EMAIL", "SUPPLIER_PORTAL", "SUPPLIER_CALL", "SUPPLIER_DOCUMENT"]),
    reference: z.string().trim().min(1).max(200),
});
const quoteFields = {
    amountMinor: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    currency: z.literal("INR"),
    description: z.string().trim().min(1).max(200),
    customerTerms,
    evidence: quoteEvidence,
    validUntil: z.iso.datetime({ offset: true }).transform((value) => new Date(value)),
};
/** S6: record and approve a quote, with the terms the customer will read. The request version is the one the staff user read. */
export const staffRecordQuoteBodySchema = z.strictObject({
    expectedVersion: version,
    ...quoteFields,
});
/** Supersede an approved quote: a reason, the version of the quote the staff user read, and the replacement. */
export const staffSupersedeQuoteBodySchema = z.strictObject({
    expectedVersion: version,
    reason: z.string().trim().min(1).max(500),
    replacement: z.strictObject(quoteFields),
    /** True: also issue the payment request of the new quote at once, as a retry of the one that was cancelled. */
    issuePaymentRequest: z.boolean(),
});
/** The payment request a quote backs, as the quote panel shows it. */
export const staffQuotePaymentRequestSchema = z.strictObject({
    id,
    status: code,
});
/** One quote as staff read it. `supplierEvidenceReference` is internal: this shape is for staff only. */
export const staffPaymentQuoteViewSchema = z.strictObject({
    id,
    conciergeRequestId: id,
    status: z.enum(["APPROVED", "WITHDRAWN"]),
    version,
    amountMinor: z.number().int().nonnegative(),
    currency: code,
    description: z.string(),
    /** `null` for a quote recorded before Phase 11. */
    customerTerms: z.string().nullable(),
    validUntil: instant,
    approvedAt: instant,
    approvedByStaffUserId: id,
    approvalBasis: z.enum(["OWNER", "SUPERVISOR"]),
    supplierEvidenceKind: z.enum([
        "SUPPLIER_EMAIL",
        "SUPPLIER_PORTAL",
        "SUPPLIER_CALL",
        "SUPPLIER_DOCUMENT",
    ]),
    supplierEvidenceReference: z.string(),
    supersedesQuoteId: id.nullable(),
    supersededByQuoteId: id.nullable(),
    withdrawnAt: instant.nullable(),
    /** sha256 hex of the quote as the amount resolver reads it (contract 2.2). Equal to the fingerprint on the payment request source. */
    fingerprint: z.string().regex(/^[0-9a-f]{64}$/u),
    /** The payment request in DRAFT, READY, PENDING_PAYMENT, PAID or PARTIALLY_REFUNDED that names this quote. */
    paymentRequest: staffQuotePaymentRequestSchema.nullable(),
});
export const staffPaymentQuoteListSchema = z.strictObject({
    quotes: z.array(staffPaymentQuoteViewSchema),
});
/**
 * The code of a 409 on a quote command (`quoteConflict.code`). The body of the refusal is the usual error envelope with one more key,
 * `quoteConflict`, so the console can say WHAT to do: reconcile first, refund first, reload the quote.
 */
export const staffQuoteConflictCodes = Object.freeze([
    "payment_quote.version_conflict",
    "payment_quote.withdrawn",
    "payment_quote.expired",
    "payment_quote.request_not_in_progress",
    "payment_quote.validity_invalid",
    "payment_quote.open_payment_exists",
    "payment_request.settlement_possible",
    "payment_quote.provider_evidence_stale",
    "request.version_conflict",
]);
export const staffQuoteConflictSchema = z.strictObject({
    code: z.enum(staffQuoteConflictCodes),
});
/** The outcome of a supersede: ids and codes only. */
export const staffSupersedeQuoteResultSchema = z.strictObject({
    quoteId: id,
    supersededQuoteId: id,
    status: z.literal("APPROVED"),
    version,
    cancelledPaymentRequestId: id.nullable(),
    paymentRequestId: id.nullable(),
});
